import { Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslatePipe } from '@ngx-translate/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatInputModule } from '@angular/material/input';
import { CONSTANTS } from '../../../../Constants/Constants';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { AccountModel, CustomerSearchRequest, CustomerSearchResponse, CustomerSearchType } from '../../../../models/dtos';
import { NotificationMessageService } from '../../../../services/common/notification-message.service';
import { ISearchCustomersData } from '../../../../models/common/ISearchCustomersData';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CRMApiService } from '../../../../services/api/crm-api.service';
import { CloseOnEscapeDirective } from '../../../../directives/close-on-escape.directive';
import { CommonDataService } from '../../../../services/common-data.service';

@Component({
  selector: 'app-customers-opportunity-dialog',
  imports: [
    TranslatePipe,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButtonModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    CloseOnEscapeDirective
  ],
  templateUrl: './search-customers-dialog.component.html',
  styleUrl: './search-customers-dialog.component.scss'
})
export class SearchCustomersDialogComponent implements OnInit {

  private readonly destroyRef = inject(DestroyRef);
  private originalCustomers: ISearchCustomersData[] = [];
  private allCustomers: ISearchCustomersData[] = [];
  private currentSort: Sort = { active: CONSTANTS.EMPTY_STRING, direction: CONSTANTS.EMPTY_STRING as 'asc' | 'desc' | '' };
  totalCount: number = CONSTANTS.ZERO;
  currentPageNumber: number = CONSTANTS.ONE;
  pageSize: number = CONSTANTS.TWENTY;
  gotoPageNumber: number | null = null;
  searchCustomersForm: FormGroup<{
    salesForceId: FormControl<string>;
    city: FormControl<string>;
    sapNumber: FormControl<string>;
    state: FormControl<string>;
    name: FormControl<string>;
    zipCode: FormControl<string>;
    address1: FormControl<string>;
  }> = new FormGroup({
    salesForceId: new FormControl(CONSTANTS.EMPTY_STRING),
    city: new FormControl(CONSTANTS.EMPTY_STRING),
    sapNumber: new FormControl(CONSTANTS.EMPTY_STRING),
    state: new FormControl(CONSTANTS.EMPTY_STRING),
    name: new FormControl(CONSTANTS.EMPTY_STRING),
    zipCode: new FormControl(CONSTANTS.EMPTY_STRING),
    address1: new FormControl(CONSTANTS.EMPTY_STRING)
  });

  dataSource: ISearchCustomersData[] = [];

  // Table configuration
  displayedColumns: string[] = ['salesForceId', 'sapNumber', 'customerName', 'address1', 'city', 'state', 'zipCode'];
  selectedRow: ISearchCustomersData | null = null;

  constructor(
    private readonly crmApiService: CRMApiService,
    private readonly dialogRef: MatDialogRef<SearchCustomersDialogComponent>,
    private readonly notificationService: NotificationMessageService,
    private readonly commonDataService: CommonDataService,
    @Inject(MAT_DIALOG_DATA) public data: CustomerSearchType
  ) { }

  ngOnInit() {
  }

  get totalPages(): number {
    return this.pageSize > CONSTANTS.ZERO ? Math.ceil(this.totalCount / this.pageSize) : CONSTANTS.ZERO;
  }

  onPageChange(event: PageEvent): void {
    this.currentPageNumber = event.pageIndex + CONSTANTS.ONE;
    this.pageSize = event.pageSize;
    this.updateDisplayedData();
  }

  onSortChange(sort: Sort): void {
    this.currentSort = sort;
    this.currentPageNumber = CONSTANTS.ONE;
    this.applySorting();
  }

  gotoPage(): void {
    const requestedPage = Number(this.gotoPageNumber);

    if (!Number.isInteger(requestedPage) || requestedPage < CONSTANTS.ONE || requestedPage > this.totalPages) {
      this.gotoPageNumber = null;
      return;
    }

    this.currentPageNumber = requestedPage;
    this.gotoPageNumber = null;
    this.updateDisplayedData();
  }

  private updateDisplayedData(): void {
    const start = (this.currentPageNumber - CONSTANTS.ONE) * this.pageSize;
    this.dataSource = this.allCustomers.slice(start, start + this.pageSize);
  }

  private applySorting(): void {
    const { active, direction } = this.currentSort;

    if (!active || !direction) {
      this.allCustomers = [...this.originalCustomers];
    } else {
      this.allCustomers = [...this.originalCustomers].sort((a, b) => {
        const valueA = this.getSortValue(a, active);
        const valueB = this.getSortValue(b, active);
        const comparison = this.compareValues(valueA, valueB);
        return direction === 'asc' ? comparison : -comparison;
      });
    }

    this.totalCount = this.allCustomers.length;
    this.updateDisplayedData();
  }

  private getSortValue(item: ISearchCustomersData, column: string): string | number {
    const value = (item as unknown as Record<string, unknown>)[column];

    if (typeof value === 'number') {
      return value;
    }

    return String(value ?? CONSTANTS.EMPTY_STRING).toLowerCase();
  }

  private compareValues(a: string | number, b: string | number): number {
    if (a < b) {
      return -CONSTANTS.ONE;
    }

    if (a > b) {
      return CONSTANTS.ONE;
    }

    return CONSTANTS.ZERO;
  }

  resetForm() {
    this.searchCustomersForm.reset();
    this.originalCustomers = [];
    this.allCustomers = [];
    this.dataSource = [];
    this.currentPageNumber = CONSTANTS.ONE;
    this.pageSize = CONSTANTS.TWENTY;
    this.gotoPageNumber = null;
    this.totalCount = CONSTANTS.ZERO;
    this.currentSort = {
      active: CONSTANTS.EMPTY_STRING,
      direction: CONSTANTS.EMPTY_STRING as 'asc' | 'desc' | ''
    };
    this.selectedRow = null;
  }

  get formControls() {
    return this.searchCustomersForm.controls;
  }

  onRowClick(row: ISearchCustomersData): void {
    this.selectedRow = row;
  }

  selectCustomer() {
    if (this.selectedRow) {
      this.dialogRef.close({ customerInfo: this.selectedRow });
    }
  }

  private createSearchRequest(): CustomerSearchRequest {
    const formValue = this.searchCustomersForm.value;

    return {
      accountModelRequest: {
        accountID: formValue.salesForceId || null,
        customerID: formValue.sapNumber || null,
        accountName: formValue.name || null,
        shippingAddress1: formValue.address1 || null,
        shippingCity: formValue.city || null,
        shippingStateProvince: formValue.state || null,
        shippingZipCode: formValue.zipCode || null
      },
      customerSearchType: this.data,
      cultureId: this.commonDataService.getEstimateCultureId()
    };
  }

  private updateTableData(accountModels: AccountModel[]): void {
    this.originalCustomers = accountModels.map(account => ({
      salesForceId: account.accountID || CONSTANTS.EMPTY_STRING,
      sapNumber: account.customerID || CONSTANTS.EMPTY_STRING,
      customerName: account.accountName || CONSTANTS.EMPTY_STRING,
      address1: account.shippingAddress1 || CONSTANTS.EMPTY_STRING,
      city: account.shippingCity || CONSTANTS.EMPTY_STRING,
      state: account.shippingStateProvince || CONSTANTS.EMPTY_STRING,
      zipCode: account.shippingZipCode || CONSTANTS.EMPTY_STRING
    }));
    this.currentPageNumber = CONSTANTS.ONE;
    this.gotoPageNumber = null;
    this.selectedRow = null;
    this.currentSort = {
      active: CONSTANTS.EMPTY_STRING,
      direction: CONSTANTS.EMPTY_STRING as 'asc' | 'desc' | ''
    };
    this.applySorting();
  }

  private hasSearchCriteria(): boolean {
    const formValue = this.searchCustomersForm.value;
    return Object.values(formValue).some(value => value && value.trim().length > 0);
  }

  searchCustomers() {
    if (!this.hasSearchCriteria()) {
      this.notificationService.showInfo(
        'PLEASE_PROVIDE_SEARCH_CRITERIA', 
        'SEARCH_CUSTOMERS',
        CONSTANTS.DEFAULT_DIALOG_WIDTH
      );
      this.updateTableData([]);
      return;
    }


    const searchRequest = this.createSearchRequest();

    this.crmApiService.searchCustomers(searchRequest)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: CustomerSearchResponse) => {
          if (response && response.accountModels && response.accountModels.length > 0) {
            this.updateTableData(response.accountModels);
          } else {
            this.updateTableData([]);
          }
        },
        error: (error) => {
          this.updateTableData([]);
        }
      });
  }

  onDblClick(row: ISearchCustomersData): void {
    this.selectedRow = row;
    this.selectCustomer();
  }
}

